import { AnchorPoint, BezierPoint, Vec2 } from "./lib/points";
import { MathHelper } from "./helpers/MathHelper";
import { cloneFabricObject } from "../../helpers/clone-handler";
import { CONTROL_POINT_TYPE } from "@frontend/lib/fabric/effects/fx/WarpEffect/constants";
import { fabric } from "fabric";

export function initWarpDeltaForObject(fabricObject) {
  fabricObject.warp = fabricObject.warp || {};
  fabricObject.warp.value = [];

  for (let j = 0; j < 4; ++j) {
    for (let i = 0; i < 4; ++i) {
      fabricObject.warp.value.push({ du: 0, dv: 0 });
    }
  }
}

export function getWarpPointsFromDeltas(fabricObject) {
  const width = fabricObject.getScaledWidth();
  const height = fabricObject.getScaledHeight();
  const deltas = fabricObject.warp?.value ?? [];
  const points = [];

  for (let j = 0; j < 4; ++j) {
    const v = j / 3;
    for (let i = 0; i < 4; ++i) {
      const u = i / 3;
      const baseX =
        u * (1 - v) * width +
        u * v * width;
      const baseY =
        u * v * height +
        (1 - u) * v * height;
      const idx = j * 4 + i;
      const { du = 0, dv = 0 } = deltas[idx] || {};
      points.push({
        x: baseX + du * width,
        y: baseY + dv * height,
        type: (i === 0 || i === 3) && (j === 0 || j === 3) ? CONTROL_POINT_TYPE.ANCHOR : CONTROL_POINT_TYPE.BEZIER
      });
    }
  }
  return points;
}

export class WarpEffect {
  GRID = 30;
  VERTS = (this.GRID + 1) ** 2;
  verts = new Float32Array(this.VERTS * 4);
  /** @type {ControlPoint[]} */
  controlPoints = [];
  /** @type {WebGLVertexArrayObject} */
  vao;
  /** @type {WebGLBuffer} */
  vbo;
  /** @type {WebGLBuffer} */
  ibo;
  drag = -1;
  quadCorners = [];
  isSmartObject = false;

  gl;
  ctx2d;

  isActive(obj) {
    const ref = obj.smartObject || obj;
    const warp = ref.warp;
    return warp && warp.render && !ref.skew?.render;
  }

  apply(obj, effectsRenderBuffer) {
    const ref = obj.smartObject || obj;

    if (!ref.warp || !ref.warp.value) {
      initWarpDeltaForObject(ref);
    }

    this.isSmartObject = !!obj.smartObject;
    this.showGrid = ref.warp.showGrid ?? false;

    let warpPoints = getWarpPointsFromDeltas(ref);
    let allPoints = warpPoints;

    const xs = allPoints.map(p => p.x);
    const ys = allPoints.map(p => p.y);

    const minX = Math.min(0, ...xs);
    const minY = Math.min(0, ...ys);
    const maxX = Math.max(
      effectsRenderBuffer.canvas ? effectsRenderBuffer.canvas.width : ref.getScaledWidth(),
      ...xs
    );
    const maxY = Math.max(
      effectsRenderBuffer.canvas ? effectsRenderBuffer.canvas.height : ref.getScaledHeight(),
      ...ys
    );

    const resultWidth = Math.ceil(maxX - minX);
    const resultHeight = Math.ceil(maxY - minY);

    const glCanvas = document.createElement('canvas');
    glCanvas.backgroundColor = 'transparent';
    glCanvas.width = resultWidth;
    glCanvas.height = resultHeight;
    this.glCanvas = glCanvas;

    const gl = this.glCanvas.getContext('webgl2', { premultipliedAlpha: false });
    if (!gl) {
      console.error('WebGL2 is not supported');
      return null;
    }
    this.gl = gl;

    let warpValue = warpPoints.map(p =>
      MathHelper.pxToGL(p.x - minX, p.y - minY, resultWidth, resultHeight)
    );
    this.gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1);

    this._initCPCorners(warpValue);
    this._initIndices();
    this._initLineIndices();
    this._createGLObjects();

    const elementData = obj.toObject();
    elementData.skew = null;
    elementData.shadow = null;
    elementData.warp = null;

    const canvasWidth = effectsRenderBuffer.canvas
      ? effectsRenderBuffer.canvas.width
      : ref.getScaledWidth();
    const canvasHeight = effectsRenderBuffer.canvas
      ? effectsRenderBuffer.canvas.height
      : ref.getScaledHeight();

    if (obj.smartObject) {
      elementData.left = obj.left - obj.smartObject.left;
      elementData.top = obj.top - obj.smartObject.top;
    } else {
      elementData.left = 0;
      elementData.top = 0;
    }

    if (obj.clipPath) {
      const cp = fabric.util.object.clone(obj.clipPath);
      cp.absolutePositioned = true;
      cp.left = (cp.left || 0) - obj.left + elementData.left;
      cp.top = (cp.top || 0) - obj.top + elementData.top;
      elementData.clipPath = cp;
    }

    const clone = cloneFabricObject(obj, elementData);

    const tempCanvas = new fabric.StaticCanvas(null, {
      width: canvasWidth,
      height: canvasHeight,
      enableRetinaScaling: false,
    });
    tempCanvas.add(clone);
    tempCanvas.renderAll();

    let source = effectsRenderBuffer.canvas ?? null;
    if (!source) {
      if (tempCanvas._element) {
        source = tempCanvas._element;
      } else if (tempCanvas.toCanvasElement) {
        source = tempCanvas.toCanvasElement();
      } else if (tempCanvas.toDataURL) {
        const img = new window.Image();
        img.src = tempCanvas.toDataURL();
        source = img;
      } else {
        console.error('Cannot extract image from fabric object');
        return null;
      }
    }

    if (source instanceof HTMLImageElement && !source.complete) {
      source.onload = () => {
        gl.bindTexture(gl.TEXTURE_2D, this.texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
        this._recalcVerts();
        this._draw();
      };
    } else {
      gl.bindTexture(gl.TEXTURE_2D, this.texture);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
      this._recalcVerts();
      this._draw();
    }

    const resultCanvas = document.createElement('canvas');
    const resultCanvasCtx = resultCanvas.getContext('2d');
    resultCanvas.width = resultWidth;
    resultCanvas.height = resultHeight;
    resultCanvasCtx.drawImage(this.glCanvas, 0, 0);

    return {
      canvas: resultCanvas,
      offsetX: -minX - (ref.left - obj.left),
      offsetY: -minY - (ref.top - obj.top)
    };
  }

  _initIndices() {
    const idx = [];
    for (let y = 0; y < this.GRID; y++) {
      for (let x = 0; x < this.GRID; x++) {
        const i = y * (this.GRID + 1) + x;
        idx.push(i, i + this.GRID + 1, i + 1);
        idx.push(i + 1, i + this.GRID + 1, i + this.GRID + 2);
      }
    }
    this.indices = new Uint16Array(idx);
  }

  _initLineIndices() {
    const lines = [];
    const step = this.GRID + 1;
    for (let y = 0; y <= this.GRID; y++)
      for (let x = 0; x < this.GRID; x++)
        lines.push(y * step + x, y * step + x + 1);
    for (let x = 0; x <= this.GRID; x++)
      for (let y = 0; y < this.GRID; y++)
        lines.push(y * step + x, (y + 1) * step + x);
    this.lineIndices = new Uint16Array(lines);
  }

  /**
   * @param {Vec2[]=} points
   */
  _initCPCorners(points) {
    if (points && points.length === 16) {
      this.controlPoints = [];
      for (let j = 0; j < 4; ++j) {
        for (let i = 0; i < 4; ++i) {
          const idx = j * 4 + i;
          const pos = points[idx];
          const main = (i === 0 || i === 3) && (j === 0 || j === 3);
          const pointKlass = main ? AnchorPoint : BezierPoint;
          this.controlPoints.push(new pointKlass(pos.x, pos.y));
        }
      }
      return;
    }

    if (!this.quadCorners || this.quadCorners.length === 0) {
      this.controlPoints = [];
      for (let j = 0; j < 4; ++j) {
        for (let i = 0; i < 4; ++i) {
          const main = (i === 0 || i === 3) && (j === 0 || j === 3);
          const pointKlass = main ? AnchorPoint : BezierPoint;
          this.controlPoints.push(
            new pointKlass(
              -1 + 2 * (i / 3),
              -1 + 2 * (j / 3)
            )
          )
        }
      }
      return;
    }
    const [A, B, C, D] = this.quadCorners;
    this.controlPoints = [];
    for (let j = 0; j < 4; ++j) {
      for (let i = 0; i < 4; ++i) {
        const u = i / 3;
        const v = j / 3;
        const pos = MathHelper.quadInterp(A, B, C, D, u, v);
        const main = (i === 0 || i === 3) && (j === 0 || j === 3);
        const pointKlass = main ? AnchorPoint : BezierPoint;
        this.controlPoints.push(new pointKlass(pos.x, pos.y));
      }
    }
  }

  _recalcVerts() {
    for (let gy = 0; gy <= this.GRID; ++gy) {
      for (let gx = 0; gx <= this.GRID; ++gx) {
        const i = gy * (this.GRID + 1) + gx;
        const u = gx / this.GRID;
        const v = gy / this.GRID;
        const cps = [];
        for (let j = 0; j < 4; ++j) {
          cps.push([
            this.controlPoints[j * 4 + 0],
            this.controlPoints[j * 4 + 1],
            this.controlPoints[j * 4 + 2],
            this.controlPoints[j * 4 + 3],
          ]);
        }
        const tmp = [];
        for (let i2 = 0; i2 < 4; ++i2)
          tmp.push(MathHelper.bezV(cps[0][i2], cps[1][i2], cps[2][i2], cps[3][i2], v));
        const p = MathHelper.bezV(tmp[0], tmp[1], tmp[2], tmp[3], u);

        this.verts[i * 4 + 0] = p.x;
        this.verts[i * 4 + 1] = p.y;
        this.verts[i * 4 + 2] = u;
        this.verts[i * 4 + 3] = 1 - v;
      }
    }
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.vbo);
    this.gl.bufferSubData(this.gl.ARRAY_BUFFER, 0, this.verts);
  }

  _createGLObjects() {
    const gl = this.gl;
    const vs = `#version 300 es
      in vec2 a_position;
      in vec2 a_texCoord;
      out vec2 v_uv;
      void main(){ gl_Position = vec4(a_position,0,1); v_uv = a_texCoord; }`;
    const fs = `#version 300 es
      precision highp float;
      in vec2 v_uv;
      uniform sampler2D u_img;
      uniform vec4 u_color;
      out vec4 o;
      void main(){
        if (u_color.a > 0.0) o = u_color;
        else o = texture(u_img, v_uv);
      }`;
    const compile = (t, s) => {
      const sh = gl.createShader(t);
      gl.shaderSource(sh, s);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) throw gl.getShaderInfoLog(sh);
      return sh;
    };
    this.program = gl.createProgram();
    gl.attachShader(this.program, compile(gl.VERTEX_SHADER, vs));
    gl.attachShader(this.program, compile(gl.FRAGMENT_SHADER, fs));
    gl.linkProgram(this.program);
    if (!gl.getProgramParameter(this.program, gl.LINK_STATUS))
      throw gl.getProgramInfoLog(this.program);

    this.vao = gl.createVertexArray();
    gl.bindVertexArray(this.vao);

    this.vbo = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferData(gl.ARRAY_BUFFER, this.verts, gl.DYNAMIC_DRAW);

    const pLoc = gl.getAttribLocation(this.program, 'a_position');
    const tLoc = gl.getAttribLocation(this.program, 'a_texCoord');
    gl.enableVertexAttribArray(pLoc);
    gl.vertexAttribPointer(pLoc, 2, gl.FLOAT, false, 16, 0);
    gl.enableVertexAttribArray(tLoc);
    gl.vertexAttribPointer(tLoc, 2, gl.FLOAT, false, 16, 8);

    this.ibo = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, this.indices, gl.STATIC_DRAW);

    if (this.showGrid && !this.isSmartObject) {
      this.lineBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.lineBuffer);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, this.lineIndices, gl.STATIC_DRAW);
    }

    this.texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(
      gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0,
      gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 255])
    );
  }

  _draw() {
    const gl = this.gl;
    gl.viewport(0, 0, this.glCanvas.width, this.glCanvas.height);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.useProgram(this.program);
    gl.bindVertexArray(this.vao);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.texture);

    const colorLoc = gl.getUniformLocation(this.program, 'u_color');
    const imgLoc = gl.getUniformLocation(this.program, 'u_img');

    gl.uniform1i(imgLoc, 0);
    gl.uniform4f(colorLoc, 0, 0, 0, 0);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ibo);
    gl.drawElements(gl.TRIANGLES, this.indices.length, gl.UNSIGNED_SHORT, 0);

    if (this.showGrid && !this.isSmartObject) {
      gl.uniform4f(colorLoc, 0, 0, 0, 1);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.lineBuffer);
      gl.drawElements(gl.LINES, this.lineIndices.length, gl.UNSIGNED_SHORT, 0);
    }
  }
}

export const initWarpControls = () => {
  const warpControlNames = [];
  for (let j = 0; j < 4; ++j) {
    for (let i = 0; i < 4; ++i) {
      warpControlNames.push(`warp_${j}_${i}`);
    }
  }

  warpControlNames.forEach((name, idx) => {
    fabric.Object.prototype.controls[name] = new fabric.Control({
      actionName: 'warp',
      sizeX: 24,
      sizeY: 24,
      getVisibility: function(fabricObject, controlKey) {
        const i = idx % 4;
        const j = Math.floor(idx / 4);
        return fabricObject.warp
          && fabricObject.warp.editing
          && !fabricObject.skew?.render
          && (i === 0 || i === 3 || j === 0 || j === 3);
      },
      positionHandler: function(dim, finalMatrix, fabricObject) {
        if (!fabricObject.warp || !fabricObject.warp.value) {
          initWarpDeltaForObject(fabricObject);
        }
        const points = getWarpPointsFromDeltas(fabricObject);
        const point = points[idx];
        if (!point) {
          return { x: 0, y: 0 };
        }
        return {
          x: point.x + fabricObject.left,
          y: point.y + fabricObject.top
        };
      },
      actionHandler: function(eventData, transform, x, y) {
        const fabricObject = transform.target;
        if (!fabricObject.warp || !fabricObject.warp.value) return false;
        const pointer = fabricObject.canvas.getPointer(eventData, true);
        const local = fabricObject.toLocalPoint(pointer, fabricObject.originX, fabricObject.originY);

        const width = fabricObject.getScaledWidth();
        const height = fabricObject.getScaledHeight();

        const i = idx % 4;
        const j = Math.floor(idx / 4);
        const u = i / 3;
        const v = j / 3;
        const baseX =
          u * (1 - v) * width +
          u * v * width;
        const baseY =
          u * v * height +
          (1 - u) * v * height;

        fabricObject.warp.value[idx].du = (local.x - baseX) / width;
        fabricObject.warp.value[idx].dv = (local.y - baseY) / height;

        fabricObject.fire('warp:changed', { warpIdx: idx, pointer });
        fabricObject.canvas.requestRenderAll();
        return true;
      },
      render: function(ctx, left, top, styleOverride, fabricObject) {
        const i = idx % 4, j = Math.floor(idx / 4);
        const warp = fabricObject.warp;
        if (!warp || !warp.value) return;

        if ((i === 0 || i === 3) && (j === 0 || j === 3)) {
          let bezierIdx1, bezierIdx2;
          if (idx === 0) {
            bezierIdx1 = 1;
            bezierIdx2 = 4;
          } else if (idx === 3) {
            bezierIdx1 = 2;
            bezierIdx2 = 7;
          } else if (idx === 12) {
            bezierIdx1 = 8;
            bezierIdx2 = 13;
          } else if (idx === 15) {
            bezierIdx1 = 11;
            bezierIdx2 = 14;
          }
          const points = getWarpPointsFromDeltas(fabricObject);
          [bezierIdx1, bezierIdx2].forEach(bi => {
            if (typeof bi === "number") {
              const p = points[idx];
              const b = points[bi];
              ctx.save();
              ctx.beginPath();
              ctx.moveTo(
                p.x + fabricObject.left,
                p.y + fabricObject.top
              );
              ctx.lineTo(
                b.x + fabricObject.left,
                b.y + fabricObject.top
              );
              ctx.strokeStyle = 'rgba(0,120,255,0.9)';
              ctx.lineWidth = 2 / fabricObject.canvas.getZoom();
              ctx.stroke();
              ctx.restore();
            }
          });
        }

        if (i === 0 || i === 3 || j === 0 || j === 3) {
          const zoom = fabricObject.canvas.getZoom();
          ctx.save();
          ctx.fillStyle = ((i === 0 || i === 3) && (j === 0 || j === 3)) ? '#ff2222' : '#0090ff';
          ctx.beginPath();
          ctx.arc(left, top, 7 / zoom, 0, 2 * Math.PI);
          ctx.fill();
          ctx.restore();
        }
      },
      cursorStyleHandler: function() {
        return 'pointer';
      },
    });
  });
}
